import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { fetchReportData } from '@/services/report/query';
import { ReportData } from '@/services/report/types';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useReportRetry } from './useReportRetry';

export function useReportData() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function loadReportData() {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);
      
      const data = await fetchReportData(currentUser.uid);
      setReportData(data);
      setError(null);
    } catch (error: any) {
      console.error('Error loading report data:', error);
      const errorMessage = error.message || 'Failed to load report data';
      setError(errorMessage);
      
      if (error.code === 'permission-denied') {
        toast.error('Access denied. Redirecting to dashboard...');
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  const { handleError, cleanup } = useReportRetry(loadReportData);

  useEffect(() => {
    loadReportData().catch(handleError);
    return cleanup;
  }, [currentUser, navigate]);

  return { reportData, loading, error };
}